import React from "react";

import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Image as ImageIcon,
  Plus,
  RotateCcw,
  Trash2,
  X,
} from "lucide-react";

const StoreModals = ({
  // =====================================================
  // ITEM MODAL
  // =====================================================

  showItemModal,
  setShowItemModal,

  formData,
  handleInputChange,

  categoryOptions,

  handleImageChange,
  removeImage,

  selectedModalImageIndex,
  setSelectedModalImageIndex,

  modalMainImage,

  resetForm,
  handleSaveItem,
  saving,

  openViewer,

  // =====================================================
  // CATEGORY MODAL
  // =====================================================

  showCategoryModal,
  setShowCategoryModal,

  newCategory,
  setNewCategory,

  handleAddCategory,
  handleDeleteCategory,

  categoryLoading,
  categories,

  // =====================================================
  // IMAGE VIEWER
  // =====================================================

  showImageViewer,
  setShowImageViewer,

  viewerImages,
  viewerIndex,
  setViewerIndex,

  prevViewerImage,
  nextViewerImage,
}) => {
  return (
    <>
      {/* =====================================================
          ADD / EDIT ITEM MODAL
      ===================================================== */}

      {showItemModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 backdrop-blur-[2px]">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-[24px] bg-white shadow-2xl">
            {/* HEADER */}

            <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white/95 px-5 py-3 backdrop-blur">
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  {formData.itemId ? "Edit Store Item" : "Add Store Item"}
                </h3>

                <p className="text-xs text-slate-500">
                  Manage pricing, category, returnable status and image gallery.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowItemModal(false)}
                className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>

            {/* BODY */}

            <div className="grid gap-4 p-4 xl:grid-cols-[1.1fr_0.9fr]">
              {/* =================================================
                  LEFT FORM
              ================================================= */}

              <div className="space-y-3">
                {/* ITEM NAME + CATEGORY */}

                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                      Item Name
                    </label>

                    <input
                      type="text"
                      value={formData.itemName}
                      onChange={(event) =>
                        handleInputChange("itemName", event.target.value)
                      }
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-orange-400"
                      placeholder="Enter item name"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                      Category
                    </label>

                    <select
                      value={formData.category}
                      onChange={(event) =>
                        handleInputChange("category", event.target.value)
                      }
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-orange-400"
                    >
                      <option value="">Select category</option>

                      {categoryOptions.map((category) => (
                        <option key={category.id} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* SIZE + COLOR */}

                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                      Size
                    </label>

                    <input
                      type="text"
                      value={formData.size}
                      onChange={(event) =>
                        handleInputChange("size", event.target.value)
                      }
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-orange-400"
                      placeholder="Normal / XL / Large"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                      Color
                    </label>

                    <input
                      type="text"
                      value={formData.color}
                      onChange={(event) =>
                        handleInputChange("color", event.target.value)
                      }
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-orange-400"
                      placeholder="Blue / Black / White"
                    />
                  </div>
                </div>

                {/* PRICE + RETURNABLE */}

                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                      Price
                    </label>

                    <input
                      type="number"
                      value={formData.price}
                      onChange={(event) =>
                        handleInputChange("price", event.target.value)
                      }
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-orange-400"
                      placeholder="Enter price"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                      Returnable
                    </label>

                    <select
                      value={formData.isReturnable}
                      onChange={(event) =>
                        handleInputChange("isReturnable", event.target.value)
                      }
                      className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-orange-400"
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>

                {/* DESCRIPTION */}

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Description
                  </label>

                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(event) =>
                      handleInputChange("description", event.target.value)
                    }
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-400"
                    placeholder="Enter item description"
                  />
                </div>

                {/* IMAGE UPLOAD */}

                <div className="rounded-2xl border border-dashed border-orange-200 bg-orange-50/60 p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900">
                        Upload Images
                      </h4>

                      <p className="text-xs text-slate-500">
                        Add multiple images and choose preview.
                      </p>
                    </div>

                    <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-orange-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-orange-600">
                      <Plus size={15} />
                      Add Images
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleImageChange}
                      />
                    </label>
                  </div>

                  {formData.image.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {formData.image.map((image, index) => {
                        const thumb = image.preview || image.url || image;

                        const active = selectedModalImageIndex === index;

                        return (
                          <div key={index} className="relative">
                            <button
                              type="button"
                              onClick={() => setSelectedModalImageIndex(index)}
                              className={`h-14 w-14 overflow-hidden rounded-xl border transition ${
                                active
                                  ? "store-thumb-active"
                                  : "border-slate-200 bg-white hover:border-orange-300"
                              }`}
                            >
                              <img
                                src={thumb}
                                alt={`modal-thumb-${index}`}
                                className="h-full w-full object-cover"
                              />
                            </button>

                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-1 text-red-500 shadow"
                            >
                              <Trash2 size={11} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* =================================================
                  RIGHT PREVIEW
              ================================================= */}

              <div className="space-y-3">
                {/* SELECTED IMAGE */}

                <div className="rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-slate-50 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-900">
                      Selected Image Preview
                    </h4>

                    {formData.image.length > 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          openViewer(
                            formData.image.map(
                              (image) => image.preview || image.url || image,
                            ),
                            selectedModalImageIndex,
                          )
                        }
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        <Eye size={13} />
                        View
                      </button>
                    )}
                  </div>

                  <div className="relative flex h-[220px] items-center justify-center overflow-hidden rounded-2xl bg-white">
                    {modalMainImage ? (
                      <img
                        src={modalMainImage}
                        alt="Selected preview"
                        className="max-h-full max-w-full object-contain p-3"
                      />
                    ) : (
                      <div className="text-center text-slate-400">
                        <ImageIcon size={34} className="mx-auto" />

                        <p className="mt-2 text-xs">No image selected</p>
                      </div>
                    )}
                  </div>

                  {formData.image.length > 1 && (
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedModalImageIndex((prev) =>
                            prev === 0 ? formData.image.length - 1 : prev - 1,
                          )
                        }
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        <ChevronLeft size={14} />
                        Prev
                      </button>

                      <p className="text-xs text-slate-500">
                        {selectedModalImageIndex + 1} / {formData.image.length}
                      </p>

                      <button
                        type="button"
                        onClick={() =>
                          setSelectedModalImageIndex((prev) =>
                            prev === formData.image.length - 1 ? 0 : prev + 1,
                          )
                        }
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Next
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  )}
                </div>

                {/* LIVE PRODUCT PREVIEW */}

                <div className="flex-1 min-h-0 overflow-hidden rounded-2xl border border-orange-100 bg-white p-3 shadow-sm">
                  <h4 className="text-sm font-semibold text-slate-900">
                    Live Product Preview
                  </h4>

                  <div className="mt-3 overflow-hidden rounded-2xl border border-orange-100 bg-[#FFF7ED]">
                    <div className="flex h-36 items-center justify-center bg-white/70">
                      {modalMainImage ? (
                        <img
                          src={modalMainImage}
                          alt="live-preview"
                          className="max-h-full max-w-full object-contain p-3"
                        />
                      ) : (
                        <ImageIcon size={30} className="text-slate-300" />
                      )}
                    </div>

                    <div className="space-y-2 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h5 className="truncate text-sm font-bold text-slate-900">
                            {formData.itemName || "Item name"}
                          </h5>

                          <p className="text-xs text-slate-500">
                            {formData.category || "No category selected"}
                          </p>
                        </div>

                        <span className="rounded-full bg-orange-100 px-2.5 py-0.5 text-[11px] font-semibold text-orange-700">
                          ₹ {formData.price || "0.00"}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-1.5">
                        <span className="rounded-full bg-white px-2.5 py-0.5 text-[11px] font-semibold text-slate-700">
                          {formData.size || "Normal"}
                        </span>

                        <span className="rounded-full bg-white px-2.5 py-0.5 text-[11px] font-semibold text-slate-700">
                          {formData.color || "No color"}
                        </span>

                        <span
                          className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                            formData.isReturnable === "Yes"
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-red-50 text-red-700"
                          }`}
                        >
                          {formData.isReturnable === "Yes"
                            ? "Returnable"
                            : "Non Returnable"}
                        </span>
                      </div>

                      <p className="line-clamp-2 text-xs leading-5 text-slate-600">
                        {formData.description ||
                          "Item description will appear here."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FOOTER */}

            <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t bg-white px-5 py-3">
              <button
                type="button"
                onClick={resetForm}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <RotateCcw size={15} />
                Reset
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowItemModal(false)}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleSaveItem}
                  disabled={saving}
                  className="rounded-xl bg-orange-500 px-4 py-2 text-xs font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {saving
                    ? "Saving..."
                    : formData.itemId
                      ? "Update Item"
                      : "Save Item"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          CATEGORY MODAL
      ===================================================== */}

      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-2xl rounded-[30px] bg-white shadow-2xl">
            {/* HEADER */}

            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h3 className="text-xl font-bold text-slate-900">
                  Manage Categories
                </h3>

                <p className="text-sm text-slate-500">
                  Add and remove categories used in store item mapping.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowCategoryModal(false)}
                className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100"
              >
                <X size={20} />
              </button>
            </div>

            {/* BODY */}

            <div className="space-y-4 p-6">
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  type="text"
                  value={newCategory}
                  onChange={(event) => setNewCategory(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      handleAddCategory();
                    }
                  }}
                  placeholder="Enter category name"
                  className="h-11 flex-1 rounded-xl border border-slate-200 px-4 text-sm outline-none focus:border-orange-400"
                />

                <button
                  type="button"
                  onClick={handleAddCategory}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
                >
                  <Plus size={16} />
                  Add Category
                </button>
              </div>

              {/* CATEGORY LIST */}

              <div className="max-h-[420px] overflow-y-auto rounded-2xl border border-orange-100">
                {categoryLoading ? (
                  <div className="space-y-3 p-4">
                    {[...Array(5)].map((_, index) => (
                      <div
                        key={index}
                        className="store-shimmer h-16 rounded-2xl"
                      />
                    ))}
                  </div>
                ) : categories.length === 0 ? (
                  <div className="p-10 text-center text-sm text-slate-500">
                    No categories found
                  </div>
                ) : (
                  <div className="divide-y divide-orange-50">
                    {categories.map((category) => (
                      <div
                        key={category.pk_category_id}
                        className="flex items-center justify-between gap-3 px-4 py-4 transition hover:bg-orange-50/50"
                      >
                        <div>
                          <p className="font-semibold text-slate-900">
                            {category.category_name}
                          </p>

                          <p className="text-xs text-slate-500">
                            ID: {category.pk_category_id}
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            handleDeleteCategory(category.pk_category_id)
                          }
                          className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          IMAGE VIEWER MODAL
      ===================================================== */}

      {showImageViewer && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
          <div className="relative w-full max-w-4xl rounded-[24px] bg-white shadow-2xl">
            {/* HEADER */}

            <div className="flex items-center justify-between border-b px-5 py-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  Image Viewer
                </h3>

                <p className="text-xs text-slate-500">
                  {viewerImages.length
                    ? `${viewerIndex + 1} / ${viewerImages.length}`
                    : ""}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowImageViewer(false)}
                className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>

            {/* IMAGE */}

            <div className="relative flex h-[58vh] items-center justify-center overflow-hidden rounded-b-[24px] bg-slate-50">
              {viewerImages?.[viewerIndex] ? (
                <img
                  src={viewerImages[viewerIndex]}
                  alt="viewer"
                  className="max-h-full max-w-full object-contain p-4"
                />
              ) : null}

              {viewerImages.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prevViewerImage}
                    className="absolute left-4 rounded-full bg-white p-2.5 text-slate-700 shadow-lg transition hover:scale-105"
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <button
                    type="button"
                    onClick={nextViewerImage}
                    className="absolute right-4 rounded-full bg-white p-2.5 text-slate-700 shadow-lg transition hover:scale-105"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}
            </div>

            {/* THUMBNAILS */}

            {viewerImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto border-t bg-white px-5 py-3">
                {viewerImages.map((image, index) => (
                  <button
                    type="button"
                    key={index}
                    onClick={() => setViewerIndex(index)}
                    className={`h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl border ${
                      viewerIndex === index
                        ? "store-thumb-active"
                        : "border-slate-200 hover:border-orange-300"
                    }`}
                  >
                    <img
                      src={image}
                      alt={`viewer-thumb-${index}`}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default StoreModals;
